import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Modal, ModalBody } from './Modal';
import { IconButton } from './IconButton';

// Command palette (Cmd+K) — design-system primitive (story 1E-1, D42). Generic:
// the caller supplies already-resolved, grouped items and handles selection, so
// this component owns only presentation + keyboard navigation + a11y. Built on
// the Modal primitive (scrim, focus return, Esc). Combobox/listbox semantics
// with aria-activedescendant so the active option is announced while focus stays
// in the input.
export interface CommandPaletteItem {
  id: string;
  title: string;
  subtitle?: string | undefined;
  group: string;
}

export function CommandPalette<T extends CommandPaletteItem>({
  open,
  onClose,
  query,
  onQueryChange,
  items,
  loading = false,
  onSelect,
  placeholder = 'Search…',
  emptyHint = 'No matches.',
}: {
  open: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  items: T[];
  loading?: boolean;
  onSelect: (item: T) => void;
  placeholder?: string;
  emptyHint?: string;
}): JSX.Element | null {
  const [active, setActive] = useState(0);

  // Reset the active row whenever the result set changes.
  useEffect(() => {
    setActive(0);
  }, [query, items.length]);

  // Group items, preserving first-seen group order.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, T[]>();
    items.forEach((item) => {
      if (!byGroup.has(item.group)) {
        byGroup.set(item.group, []);
        order.push(item.group);
      }
      (byGroup.get(item.group) as T[]).push(item);
    });
    return order.map((group) => [group, byGroup.get(group) as T[]] as const);
  }, [items]);

  if (!open) return null;

  const step = (delta: number) => {
    if (items.length === 0) return;
    setActive((i) => (i + delta + items.length) % items.length);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[active];
      if (item) onSelect(item);
    }
  };

  const activeItem = items[active];

  return (
    <Modal label="Search" onClose={onClose} size="lg">
      {/* Story 2-147g: no `ModalHeader` here — a command palette's "header" is
          the search input itself, not a title + close-button row, so this
          keeps the search row's own existing shape (icon + input) and only
          adds the shared close affordance (`IconButton`, same as every other
          Modal's header) beside it, rather than forcing a title-shaped
          primitive onto a combobox. `ModalBody` still wraps the results list
          so it scrolls independently and the search row stays pinned. */}
      <div onKeyDown={onKeyDown} className="flex min-h-0 flex-1 flex-col">
        <div className="mb-3 flex shrink-0 items-center gap-2 border-b border-border/60 pb-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" aria-hidden />
          <input
            autoFocus
            role="combobox"
            aria-expanded
            aria-controls="cmdk-listbox"
            aria-activedescendant={activeItem ? `cmdk-opt-${activeItem.id}` : undefined}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={placeholder}
            className="w-full bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
          />
          <IconButton onClick={onClose} aria-label="Close" icon={<XMarkIcon className="h-5 w-5" />} className="text-muted-foreground hover:text-foreground" />
        </div>

        <ModalBody>
        <ul id="cmdk-listbox" role="listbox" aria-label="Search results" className="space-y-3">
          {loading && <li className="px-1 py-2 text-sm text-muted-foreground">Searching…</li>}
          {!loading && query.length > 0 && items.length === 0 && (
            <li className="px-1 py-2 text-sm text-muted-foreground">{emptyHint}</li>
          )}
          {groups.map(([group, groupItems]) => (
            <li key={group}>
              <p className="px-1 pb-1 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">{group}</p>
              <ul className="space-y-1">
                {groupItems.map((item) => {
                  const index = items.indexOf(item);
                  const isActive = index === active;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        id={`cmdk-opt-${item.id}`}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => onSelect(item)}
                        className={`focus-ring flex w-full flex-col rounded-xl px-3 py-2 text-left transition ${
                          isActive ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-primary/[0.08]'
                        }`}
                      >
                        <span className="text-sm font-medium text-foreground">{item.title}</span>
                        {item.subtitle && <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
        </ModalBody>
      </div>
    </Modal>
  );
}
